import { MediaType, MfaStatus } from '@qse/api'
import { SVGProps } from 'react'

import { UserRoleEnum } from './roles'

// Key generation types
export enum KeyTypeEnum {
  RSA_4096 = 'RSA-4096',
  AES_256 = 'AES-256',
}

export const KEY_TYPES = {
  RSA_4096: KeyTypeEnum.RSA_4096,
  AES_256: KeyTypeEnum.AES_256,
} as const

export type KeyType = (typeof KEY_TYPES)[keyof typeof KEY_TYPES]

export enum ModelControlEnum {
  CONFIRMATION = 'confirmation',
  ADD_USER = 'add-user',
  ADD_TENANT = 'add-tenant',
  VIEW_TENANT = 'view-tenant',
  API_KEY_DETAILS = 'api-key-details',
  CREATE_FOLDER = 'create-folder',
  MOVE_ITEMS = 'move-items',
  MOVE_CONFLICT = 'move-conflict',
  RENAME_FILE = 'rename-file',
  CREATE_PLAN = 'create-plan',
  UPDATE_PASSWORD = 'update-password',
  ADD_RESELLER = 'add-reseller',
  TWO_FA_SETUP = 'two-fa-setup',
  TWO_FA_VERIFY = 'two-fa-verify',
  TWO_FA_DISABLE = 'two-fa-disable',
  BILLING = 'billing',
  ACCESS_DENIED = 'access-denied',
  NAVIGATION_WARNING = 'navigation-warning',
  QUOTA_EXCEEDED = 'quota-exceeded',
  FILE_PREVIEW = 'file-preview',
  TRANSFER_ADMIN = 'transfer-admin',
  DELETE_USER_CONFIRMATION = 'delete-user-confirmation',
  DELETE_TENANT_CONFIRMATION = 'delete-tenant-confirmation',
  DRIVE_SETUP = 'drive-setup',
  FOLDER_FAILURES = 'folder-failures',
  BULK_INVITE = 'bulk-invite',
}

export type IconSvgProps = SVGProps<SVGSVGElement> & {
  size?: number
}

export type FileType = {
  extension: MediaType
  mediaType: string[]
  icon: (size: number) => React.ReactElement
}

export type FileTypeIconProps = {
  filename?: string
  mediaType?: string
  filetype?: string
  mimeType?: string
  className?: string
  size?: number
}

export interface MarqueeCoords {
  x: number
  y: number
}
export interface AxiosErrorObject {
  response?: {
    data?: {
      error?: any
    }
    status?: number
  }
  config?: any
  message?: string
}

// Drive and Quota interfaces
export type DriveStatus = 'READY' | 'INITIALIZING' | 'ERROR' | 'SUSPENDED'

export interface FormattedStorage {
  used: {
    GB: string
    MB: string
    KB: string
  }
  quota: {
    GB: string
    MB: string
    KB: string
  }
  remaining: {
    GB: string
    MB: string
    KB: string
  }
  pending: {
    GB: string
    MB: string
    KB: string
  }
}

export interface StorageQuota {
  used: number
  quota: number
  utilizationPercent: number
  status: string
  remaining: number
  formatted: FormattedStorage
  pendingBytes: number
}

export interface ServiceQuota {
  used: number
  quota: number
  utilizationPercent: number
  status: string
  remaining: number
}

export interface QuotaSummary {
  storage: StorageQuota
  service: ServiceQuota
  lastUpdated?: string
}

export interface Drive {
  status: DriveStatus
  quotaSummary?: QuotaSummary | null
}

export interface User {
  id: string
  avatar: string
  imageUrl?: string
  email: string
  usagePercent: number
  status: string
  lastActive: string
  firstName?: string
  lastName?: string
  phoneNumber?: string
  country?: string
  city?: string
  role?: string
  isVerified?: boolean
  createdAt?: string
  updatedAt?: string
  storageLimit: number
  storageUsed: number
  actions: string[]
  team?: string
  mfaStatus?: MfaStatus
  userRole: UserRoleEnum
  resellerId?: string
  /** True for the tenant's primary admin — a stronger role than a plain TENANT_ADMIN. */
  isPrimary?: boolean
  isActive?: boolean
  type?: string
  storageQuota?: number
  qpaQuota?: {
    qpaSeeds: number
  }
  apiQuota?: number
  drive?: Drive
  tenant?: {
    id: string
    name: string
    status:
      | 'pending_confirmation'
      | 'incomplete'
      | 'trialing'
      | 'active'
      | 'suspended'
      | 'cancelled'
    billingStatus?: 'past_due' | 'test'
    resellerId?: string
    planId?: string
  }
}

export type UserStore = {
  user: User | null
  isLoading: boolean
  error: string | null
  isAuthenticated: boolean
  requiresBilling: boolean
  setUser: (user: User | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setAuthenticated: (authenticated: boolean) => void
  setRequiresBilling: (requiresBilling: boolean) => void
  setUserAndAuth: (user: User | null, authenticated: boolean) => void
  checkAuthenticationStatus: () => boolean
  checkBillingRequirement: () => boolean
  reset: () => void
}

export interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
}

export interface LoginData {
  email: string
  password: string
}

export interface ActiveModelState {
  toggleModel: boolean
  modelActive: ModelControlEnum
  modelContent: string
  scrollBehavior?: string
  classNames: { className: string; classNameContent: string }
  width: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any
  // Modal customization
  modalHeader: ModalHeader
  modalActions: ModalAction[]
  // Track previous modal state for nested operations
  previousModalState: { modelActive: ModelControlEnum; data: any } | null
  setToggleModel: (toggle: boolean) => void
  setClassNames: (classNames: { className: string; classNameContent: string }) => void
  setModelActive: (drawerName: ModelControlEnum, additionalData?: any) => void
  setModelContent: (content: string) => void
  setWidth: (width: string) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setData: (data: any) => void
  setModalHeader: (header: ModalHeader) => void
  setModalActions: (actions: ModalAction[]) => void
  setScrollBehavior: (behavior: 'inside' | 'outside') => void
  toggle: () => void
  closeAllModals: () => void
  reset: () => void
}

// Modal Header Type
export interface ModalHeader {
  title: string
  titleTooltip?: string
  subtitle?: string
  showCloseButton?: boolean
  /** Show an expand/collapse control that toggles the modal to full screen. */
  showExpandButton?: boolean
  className?: string
}

// Modal Action Type
export interface ModalAction {
  label: string
  variant?: 'solid' | 'bordered' | 'light' | 'flat' | 'faded' | 'shadow' | 'ghost'
  color?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  onPress: (values?: any) => void
  isLoading?: boolean
  type?: 'button' | 'submit'
  disabled?: boolean
}

// Conflict Resolution Types
export type ConflictAction = 'skip' | 'replace' | 'cancel'

export interface ConflictedFile {
  id: string
  name: string
}

export interface MoveConflictModalData {
  conflictedFiles: ConflictedFile[]
  originalRequest: {
    fileIds: string[]
    targetParentId?: string
    targetParentPath?: string
  }
  onResolutionComplete: (
    finalRequest: {
      fileIds: string[]
      targetParentId?: string
      targetParentPath?: string
      force?: boolean
    } | null,
  ) => void
}

// Modal Types
export interface ModalComponentProps {
  setModelActive: (model: ModelControlEnum | undefined, additionalData?: any) => void
  data: any
  setToggleModel: (toggle: boolean) => void
  toggleModel: boolean
  onClose: () => void
  reset: () => void
  action?: 'create' | 'edit'
}

export interface ModelControlProps {
  control: ModelControlEnum
  setModelActive: (model: ModelControlEnum, additionalData?: any) => void
  setToggleModel: (toggle: boolean) => void
  toggleModel: boolean
  data: any
  onClose: () => void
  reset: () => void
}

export interface ModalData {
  modalHeader: {
    title: string
    titleTooltip?: string
    subtitle?: string
    showCloseButton?: boolean
    /** Show an expand/collapse control that toggles the modal to full screen. */
    showExpandButton?: boolean
  }
  title?: string
  message?: string
  icon?: string
  actions?: Array<{
    label: string
    color?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'
    variant?: 'solid' | 'bordered' | 'light' | 'flat' | 'faded' | 'shadow' | 'ghost'
    onPress?: () => void
    type?: 'button' | 'submit'
    isLoading?: boolean
    disabled?: boolean
  }>
  body?: React.ReactNode | ((config: ModalData) => React.ReactNode)
  theme?: 'default' | 'success' | 'warning' | 'danger' | 'info'
  /**
   * Custom classNames for different parts of the confirmation modal
   */
  classNames?: {
    /** Root container className */
    container?: string
    /** Inner content wrapper className */
    wrapper?: string
    /** Icon container className */
    iconWrapper?: string
    /** Icon image className */
    icon?: string
    /** Title className */
    title?: string
    /** Body content wrapper className */
    body?: string
    /** Message paragraph className */
    message?: string
    /** Actions container className */
    actions?: string
  }
  /**
   * Callback function that runs before the modal closes.
   * Return true to allow closing, false to prevent closing.
   * Can be async for validation operations.
   */
  onBeforeClose?: () => boolean | Promise<boolean>
}

export interface Column {
  name: string
  uid:
    | 'user'
    | 'contactEmail'
    | 'usage'
    | 'status'
    | 'lastActive'
    | 'actions'
    | 'name'
    | 'email'
    | 'role'
    | 'team'
    | 'billingPlan'
    | 'storageLimit'
    | 'apiUsage'
    | 'storageUsage'
    | 'id'
    | 'description'
    | 'stripePlanId'
    | 'storageQuotaGb'
    | 'serviceUsageQuota'
    | 'monthlyPriceCents'
    | 'isActive'
    | 'createdAt'
    | 'updatedAt'
    | 'productName'
    | 'tenantName'
    | 'type'
    | 'planName'
    | 'billingPeriod'
    | 'users'
    | 'registrationDate'
    | 'audience'
    | 'qpaSeedCount'
    | 'qpaServices'
    | 'tenantCount'
  sortable?: boolean
}

// WebSocket Real-time Event Types
export enum WebSocketEventType {
  FILE_ENCRYPTION_COMPLETED = 'FILE_ENCRYPTION_COMPLETED',
  FILE_ENCRYPTION_FAILED = 'FILE_ENCRYPTION_FAILED',
  FILE_ENCRYPTION_IN_PROGRESS = 'FILE_ENCRYPTION_IN_PROGRESS',
  FILE_COPIED = 'FILE_COPIED',
  FILE_READY_FOR_DOWNLOAD = 'FILE_READY_FOR_DOWNLOAD',
  FILE_DOWNLOAD_REQUEST_FAILED = 'FILE_DOWNLOAD_REQUEST_FAILED',
  FILE_COPY_FAILED = 'FILE_COPY_FAILED',
  FOLDER_UPLOAD_PROGRESS = 'FOLDER_UPLOAD_PROGRESS',
  USER_STORAGE_QUOTA_UPDATED = 'USER_STORAGE_QUOTA_UPDATED',
  USER_SERVICE_USAGE_UPDATED = 'USER_SERVICE_USAGE_UPDATED',
  CHECKOUT_FAILED = 'CHECKOUT_FAILED',
  CHECKOUT_COMPLETED = 'CHECKOUT_SUCCESS',
  USER_SUSPENDED = 'USER_SUSPENDED',
  TENANT_SUSPENDED = 'TENANT_SUSPENDED',
  RSA_KEYS_READY = 'RSA_KEYS_READY',
  RSA_KEYS_GENERATION_FAILED = 'RSA_KEYS_GENERATION_FAILED',

  BILLING_PAST_DUE = 'BILLING_PAST_DUE',
  BILLING_UNPAID = 'BILLING_UNPAID',
  BILLING_CANCELED = 'BILLING_CANCELED',
  PROFILE_PICTURE_THUMBNAILS_READY = 'PROFILE_PICTURE_THUMBNAILS_READY',
  QUOTA_ADJUSTMENT = 'QUOTA_ADJUSTMENT',
  QUOTA_SCHEDULED_FOR_ADJUSTMENT = 'QUOTA_SCHEDULED_FOR_ADJUSTMENT',
  BULK_INVITE_PROGRESS = 'BULK_INVITE_PROGRESS',
  BULK_INVITE_COMPLETED = 'BULK_INVITE_COMPLETED',
}

export interface WebSocketMessage {
  event: WebSocketEventType
  data:
    | FileEncryptionCompletedPayload
    | FileEncryptionFailedPayload
    | FileEncryptionInProgressPayload
    | FileCopiedPayload
    | FileReadyForDownloadPayload
    | UserStorageQuotaUpdatedPayload
    | UserServiceUsageUpdatedPayload
    | CheckoutFailedPayload
    | CheckoutCompletedPayload
    | UserSuspendedPayload
    | TenantSuspendedPayload
    | RsaKeysReadyPayload
    | RsaKeysGenerationFailedPayload
    | FolderUploadProgressPayload
    | ProfilePictureThumbnailsReadyPayload
    | QuotaAdjustmentPayload
    | QuotaScheduledForAdjustmentPayload
    | BulkInviteProgressPayload
    | BulkInviteCompletedPayload
    | {
        file: {
          id: string
          path: string
          parentId: string
          name: string
          isDir: boolean
          status: string
          sizeBytes?: number
          isFavorite: boolean
          mimeType?: string
          mediaType?: string
          createdAt: string
          updatedAt: string
          trashedAt?: string | null
          effectivelyTrashed: boolean
        }
        url?: string
        download?: {
          id: string
          status: 'PROCESSING' | 'AVAILABLE' | 'EXPIRED'
        }
        error?: string
      }
}

export interface FolderUploadProgressPayload {
  file: {
    id: string
    path: string
    parentId: string
    name: string
    isDir: boolean
    status: string
    sizeBytes?: number
    isFavorite: boolean
    mimeType?: string
    mediaType?: string
    createdAt: string
    updatedAt: string
    trashedAt?: string | null
    effectivelyTrashedBy: string | null
  }
  sessionId: string
  folderId: string
  folderName: string
  status: string
  totalFiles: number
  totalSizeBytes: number
  progress: {
    encryption: {
      completed: number
      total: number
      failed: number
      completedFileIds?: string[]
      encryptingFileIds?: string[]
      failedFileIds?: string[]
    }
    upload: {
      completed: number
      total: number
      failed: number
    }
    permanentlyDeletedFileIds?: string[]
  }
  timestamp: string
}

export interface FileEncryptionInProgressPayload {
  fileId: string
  progress: {
    percentage: number
    bytesProcessed: number
    totalBytes: number
    throughput: number
    estimatedTimeRemaining: number
    timestamp: string
  }
}

export interface FileEncryptionCompletedPayload {
  file: {
    id: string
    path: string
    parentId: string
    name: string
    isDir: boolean
    status: string
    sizeBytes?: number
    isFavorite: boolean
    mimeType?: string
    mediaType?: string
    createdAt: string
    updatedAt: string
    trashedAt?: string | null
    effectivelyTrashed: boolean
  }
}

export interface FileEncryptionFailedPayload {
  file: {
    id: string
    path: string
    parentId: string
    name: string
    isDir: boolean
    status: string
    sizeBytes?: number
    isFavorite: boolean
    mimeType?: string
    mediaType?: string
    createdAt: string
    updatedAt: string
    trashedAt?: string | null
    effectivelyTrashed: boolean
  }
  error?: string
}

export interface FileCopiedPayload {
  file: {
    id: string
    path: string
    parentId: string
    name: string
    isDir: boolean
    status: string
    sizeBytes?: number
    isFavorite: boolean
    mimeType?: string
    mediaType?: string
    createdAt: string
    updatedAt: string
    trashedAt?: string | null
    effectivelyTrashed: boolean
  }
}

export interface FileReadyForDownloadPayload {
  file: {
    id: string
    path: string
    parentId: string
    name: string
    isDir: boolean
    status: string
    sizeBytes?: number
    isFavorite: boolean
    mimeType?: string
    mediaType?: string
    createdAt: string
    updatedAt: string
    trashedAt?: string | null
    effectivelyTrashed: boolean
  }
  url: string
  download?: {
    id: string
    status: 'PROCESSING' | 'AVAILABLE' | 'EXPIRED'
  }
}

export interface UserStorageQuotaUpdatedPayload {
  quota: {
    storage: StorageQuota
    service: ServiceQuota
    lastUpdated: string
  }
  eventDetails: {
    reservationId: string
    storageBytes: number
    operationType: string
  }
  timestamp: string
}

export interface UserServiceUsageUpdatedPayload {
  quota: {
    storage: StorageQuota
    service: ServiceQuota
    lastUpdated: string
  }
  eventDetails: any
  timestamp: string
}

export interface ProfilePictureThumbnailsReadyPayload {
  profilePicture: {
    uuid: string
    updatedAt: string
    processing: boolean
    urls: {
      original: string
      small?: string
      medium?: string
      large?: string
    }
  }
}

export interface CheckoutFailedPayload {
  planId: string
  reason: string
  timestamp?: string
}

export interface CheckoutCompletedPayload {
  planId: string
  sessionId: string
  customerId?: string
  timestamp?: string
}

export interface UserSuspendedPayload {
  timestamp: string
}

export interface TenantSuspendedPayload {
  tenantId: string
  companyName: string
  suspensionDate: string // ISO format
  message: string
}

export interface WebSocketConnectionState {
  isConnected: boolean
  isConnecting: boolean
  error: string | null
  connectionId: string | null
}

export interface RsaKeysReadyPayload {
  keys: {
    private: string
    public: string
  }
  timestamp: string
}

export interface RsaKeysGenerationFailedPayload {
  error: string
  timestamp: string
}

export interface QuotaAdjustmentPayload {
  previousQuotaGb: number
  newQuotaGb: number
  usageGb: number
  isOverQuota: boolean
  isZeroAllocation: boolean
  message: string
}

export interface QuotaScheduledForAdjustmentPayload {
  targetPlan: {
    id: string
    name: string
    storageQuotaBytes: number
    serviceUsageQuota: number
  }
}

/**
 * Emitted to the initiator's web session at each worker progress checkpoint
 * while a bulk user invitation batch is PROCESSING, plus one start frame when
 * the worker picks the job up. Drives in-place cache patches so the UI does
 * not need to poll; BULK_INVITE_COMPLETED carries the terminal result.
 */
export interface BulkInviteProgressPayload {
  requestId: string
  status: 'PROCESSING'
  totalRows: number
  /** Rows attempted so far — successCount + failureCount at the checkpoint. */
  processedRows: number
  successCount: number
  failureCount: number
  /** Rows never attempted — invalid, already invited, or already registered. */
  skippedCount: number
}

/**
 * Emitted to the initiator's web session when an async bulk user invitation
 * finishes processing. Carries only terminal statuses. Best-effort — polling
 * the status endpoint remains the reliable fallback.
 */
export interface BulkInviteCompletedPayload {
  requestId: string
  status: 'SUCCEEDED' | 'PARTIAL' | 'FAILED'
  totalRows: number
  successCount: number
  failureCount: number
  /** Rows never attempted — invalid, already invited, or already registered. */
  skippedCount: number
  emailsNotSent: number
}
